import { useRef, useState, useCallback, useEffect } from "react";
import { ArrowRight, MousePointer2 } from "lucide-react";
import { ASSETS } from "../lib/constants";

export default function Hero() {
  const [pos, setPos] = useState(55); // percent
  const wrapRef = useRef(null);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(5, Math.min(95, p)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const onUp = () => (draggingRef.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [setFromClientX]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 jn-grid-bg opacity-40 pointer-events-none" />
      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(0,229,255,0.18), transparent)",
        }}
      />
      <div
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side, rgba(197,160,89,0.14), transparent)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: text */}
          <div className="lg:col-span-6">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-cyan-400 mb-6">
              Turn visibility into impact
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-[1] tracking-tight"
              data-testid="hero-headline"
            >
              Van inkijk naar{" "}
              <span
                className="italic font-medium"
                style={{ color: "#00E5FF" }}
              >
                privacy
              </span>{" "}
              met één klik.
            </h1>
            <p className="mt-6 md:mt-8 text-base md:text-lg text-zinc-400 max-w-xl font-body leading-relaxed">
              JYNOVA transformeert glas in slimme privacyoplossingen voor
              woningen, kantoren en praktijkruimtes — zonder natuurlijk licht
              te verliezen.
            </p>
            <p className="mt-4 text-xs sm:text-sm font-body text-zinc-500 tracking-wide">
              <span className="text-white">Privacy Smart Film</span>{" "}
              <span className="text-zinc-600">•</span>{" "}
              <span className="text-white">Display Glass</span>{" "}
              <span className="text-zinc-600">•</span>{" "}
              <span className="text-white">Smart Wall Panels</span>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollTo("lead-form")}
                className="jn-btn-primary inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm md:text-base"
                data-testid="hero-cta-primary"
              >
                Vraag prijsinschatting aan
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => scrollTo("hoe-werkt-het")}
                className="jn-btn-ghost px-7 py-4 rounded-full text-sm md:text-base"
                data-testid="hero-cta-secondary"
              >
                Bekijk hoe het werkt
              </button>
            </div>

            <div className="mt-12 flex items-center gap-6 text-xs text-zinc-500 font-body">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                Woningen
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: "#C5A059" }}
                />
                Kantoren & praktijken
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                Retail & horeca
              </div>
            </div>
          </div>

          {/* Right: split-slider */}
          <div className="lg:col-span-6">
            <div
              ref={wrapRef}
              className="relative aspect-[4/3] w-full rounded-3xl overflow-hidden select-none cursor-ew-resize border border-white/10"
              onMouseDown={(e) => {
                draggingRef.current = true;
                setFromClientX(e.clientX);
              }}
              onTouchStart={(e) => {
                draggingRef.current = true;
                setFromClientX(e.touches[0].clientX);
              }}
              data-testid="hero-split-slider"
            >
              {/* Base image (transparent / full visibility) */}
              <img
                src={ASSETS.hero}
                alt="Privacy Smart Film — woonkamer met inkijk vs privacy"
                className="absolute inset-0 w-full h-full object-cover"
                draggable="false"
              />

              {/* Frosted overlay - clipped by pos */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  clipPath: `inset(0 0 0 ${pos}%)`,
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(220,230,240,0.92), rgba(200,215,225,0.88))",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                  }}
                />
                <div
                  className="absolute inset-0 mix-blend-overlay opacity-25"
                  style={{
                    background:
                      "repeating-linear-gradient(45deg, rgba(255,255,255,0.3) 0 2px, transparent 2px 6px)",
                  }}
                />
              </div>

              {/* Labels */}
              <div className="absolute left-4 top-4 md:left-6 md:top-6 text-[11px] md:text-xs uppercase tracking-[0.25em] text-white/90 bg-black/40 backdrop-blur px-3 py-1.5 rounded-full border border-white/20">
                Transparant
              </div>
              <div className="absolute right-4 top-4 md:right-6 md:top-6 text-[11px] md:text-xs uppercase tracking-[0.25em] text-zinc-800 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full">
                Privacy
              </div>

              {/* Bottom center label */}
              <div className="absolute left-1/2 -translate-x-1/2 bottom-4 md:bottom-6 text-[11px] md:text-sm text-white bg-black/60 backdrop-blur px-4 py-2 rounded-full border border-white/10 flex items-center gap-2 max-w-[90%] text-center">
                <MousePointer2 size={14} className="text-cyan-400 shrink-0" />
                <span className="truncate">
                  Met één klik van zichtbaar naar privé
                </span>
              </div>

              {/* Divider */}
              <div
                className="absolute top-0 bottom-0 w-px bg-cyan-400"
                style={{ left: `${pos}%` }}
              >
                <div
                  className="jn-slider-handle absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-black/80 backdrop-blur flex items-center justify-center border border-cyan-400/60"
                  data-testid="hero-slider-handle"
                >
                  <div className="flex gap-0.5">
                    <span className="block w-0.5 h-4 bg-cyan-400 rounded" />
                    <span className="block w-0.5 h-4 bg-cyan-400 rounded" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
